<template>
  <div class="notification-settings-cont">
    <section-title title="Subscription Groups" icon="fas fa-users"/>
    <div class="notification-settings-section" :style="{color: $vuetify.theme.dark ? '#FFFFFF' : '#000000'}">
      <template v-if="!isLoadingData && (getSubscriptionGroups.length > 0)">
        <subscription-group
            v-for="(subscriptionGroupItem, subscriptionGroupIndex) in subscriptionGroupsList"
            :key="`subscription-group-key-${subscriptionGroupItem.id}`"
            :name="subscriptionGroupItem.name"
            :description="subscriptionGroupItem.description"
            :tenant="subscriptionGroupItem.tenant.name"
            :channels="subscriptionGroupItem.channels"
            @assign="assignChannelSubscriptionGroupHandler(subscriptionGroupItem)"
            @edit="editSubscriptionGroupHandler(subscriptionGroupItem)"
            @delete="deleteSubscriptionGroupHandler(subscriptionGroupIndex)"
        />
      </template>
      <template v-if="!isLoadingData && (getSubscriptionGroups.length === 0)">
        <div class="no-results">No Subscription Groups detected!</div>
      </template>
      <div
          class="add-subscription-clickable"
          :class="$vuetify.theme.dark ? 'dark':'light'"
          @click="openGroupEditorHandler"
      >
        + add
      </div>
    </div>
    <section-title title="Subscribers (channels)" icon="fas fa-user"/>
    <div class="notification-settings-section" :style="{color: $vuetify.theme.dark ? '#FFFFFF' : '#000000'}">
      <subscribers/>
    </div>
    <v-dialog
        v-model="showSubGroupEditor"
        persistent
        max-width="600px"
    >
      <v-card class="accent">
        <v-card-title
            style="
              background-color: #47bd96;
              color: white;
              letter-spacing: 0.03rem;
              height: 40px;
            "
            :style="
              $vuetify.breakpoint.xsOnly ? 'text-align: center !important;' : ''
            "
        >
          {{ editMode ? 'Edit Group' : 'Add Group' }}
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row
                justify="center"
            >
              <v-col
                  cols="12"
                  sm="6"
                  md="8"
              >
                <v-text-field
                    ref="subscriptionGroupName"
                    label="Name"
                    v-model="subscriptionGroupData.name"
                    filled
                    outlined
                    dense
                    required
                    :rules="[rules.required]"
                />
                <v-textarea
                    name="subscriptionGroupDescription"
                    label="Description"
                    v-model="subscriptionGroupData.description"
                    filled
                    outlined
                    dense
                ></v-textarea>
              </v-col>
            </v-row>

            <v-row
                justify="center"
            >
              <v-col
                  cols="12"
                  sm="6"
                  md="8"
              >
                <input-wrapper-2
                    label="Owner"
                    info="Select a Tenant for which you want the group to belong to."
                    :label-background-light="'#D2E2DE'"
                    :label-background-dark="'#162627'"
                    dense
                    full-width
                >
                  <v-select
                      ref="selectOwnerInput"
                      v-model="subscriptionGroupData.owner"
                      :items="tenantOptions"
                      item-text="label"
                      item-value="value"
                      placeholder="Select Owner"
                      dense
                      filled
                      solo
                      hide-details
                      required
                      :rules="[rules.required]"
                      style="width: 100%; margin: 4px 0;"
                  ></v-select>
                  <v-btn icon small @click="subscriptionGroupData.owner=null">
                    <v-icon small color="#d50101">
                      fas fa-times
                    </v-icon>
                  </v-btn>
                </input-wrapper-2>
              </v-col>
            </v-row>
            <v-row justify="center">
              <v-col
                  cols="12"
                  sm="6"
                  md="8"
              >
                <input-wrapper
                    label="Active"
                    container-class="pt-8"
                    :label-background="$vuetify.theme.dark ? 'rgb(30,30,30)' : '#FFFFFF'"
                    filled
                >
                  <v-switch
                      ref="groupActiveSwitchInput"
                      v-model="subscriptionGroupData.active"
                      :label="subscriptionGroupData.active === true ? 'Yes':'No'"
                      style="margin-top: 0"
                      hide-details
                  />
                </input-wrapper>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn plain @click="closeGroupEditorHandler">Cancel</v-btn>
          <span @mouseenter="touchError">
          <v-btn
              color="primary"
              :disabled="(!subscriptionGroupData.name || (!!subscriptionGroupData.name && (subscriptionGroupData.name.length < 1)))"
              :loading="saving"
              @click="saveSubscriptionGroup"
          >
            {{ !!editMode ? 'Save' : 'Add' }}
          </v-btn>
          </span>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
        v-model="showConfirmDelete"
        max-width="600px"
    >
      <v-card>
        <v-card-title
            style="
              background-color: #1c6e59;
              color: white;
              letter-spacing: 0.03rem;
              height: 40px;
            "
            :style="
              $vuetify.breakpoint.xsOnly ? 'text-align: center !important;' : ''
            "
        >
          <v-icon small class="mr-2">fas fa-exclamation-triangle</v-icon>
          Delete "{{ subscriptionGroupData.name }}"?
        </v-card-title>
        <v-card-actions>
          <v-spacer/>
          <v-btn plain @click="closeGroupEditorHandler">Cancel</v-btn>
          <v-btn
              color="red"
              :loading="saving"
              @click="deleteSubscriptionGroup"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <channel-assign
        :show="showAssignChannelDialog"
        :group-data="selectedSubscriptionGroup"
        @close="showAssignChannelDialog = false"
    />
  </div>
</template>

<script>
import SectionTitle from "@/components/Settings/SectionTitle.vue";
import {NotificationService} from "@/services/ApiService";
import SubscriptionGroup from "@/components/Settings/Notifications/SubscriptionGroup.vue";
import Subscribers from "@/components/Settings/Notifications/Subscribers.vue";
import InputWrapper from "@/components/UI/InputWrapper.vue";
import InputWrapper2 from "@/components/UI/InputWrapper2.vue";
import ChannelAssign from "@/components/Settings/Notifications/ChannelAssign.vue";
import {mapActions, mapGetters, mapMutations} from "vuex";
import {cloneDeep} from "lodash";

export default {
  name: 'NotificationSettings',
  components: {ChannelAssign, Subscribers, InputWrapper2, InputWrapper, SubscriptionGroup, SectionTitle},
  data: () => ({
    loading: true,
    saving: false,
    tenantsListOptions: [],
    showAssignChannelDialog: false,
    showSubGroupEditor: false,
    showConfirmDelete: false,
    isActiveSwitchedFocused: false,
    selectedSubscriptionGroup: null,
    subscriptionGroupData: {
      id: '',
      name: '',
      description: '',
      active: false,
    },
    rules: {
      required: (v) => !!v || "Required",
    },
  }),
  computed: {
    ...mapGetters("notifications", [
      "getTenants",
      "getChannels",
      "getChannelTypes",
      "getChannelTypeTenants",
      "getSubscriptionGroups",
      "isLoadingData",
    ]),
    subscriptionGroupsList() {
      if (!!this.getSubscriptionGroups && this.getSubscriptionGroups.length > 0) {
        let subscriptionGroupsBuffer = cloneDeep(this.getSubscriptionGroups);
        return subscriptionGroupsBuffer.sort(function (a, b) {
          var textA = a.name.toUpperCase();
          var textB = b.name.toUpperCase();
          return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });
      }
      return [];
    },
    editMode() {
      return this.selectedSubscriptionGroup !== null;
    },
    tenantOptions() {
      if (!this.getTenants || this.getTenants.length < 1) return [];
      return this.getTenants.map(tenantItem => {
        return {label: tenantItem.name, value: tenantItem.id};
      });
    },
  },
  methods: {
    ...mapActions("notifications", ["prepareData"]),
    ...mapMutations("notifications", [
      "setTenants",
      "setChannels",
      "setChannelTypes",
      "setChannelTypeTenants",
      "setSubscriptionGroups",
    ]),
    /**
     * Check if the user has entered a name for the subscription group
     * and display the corresponding error message accordingly
     */
    touchError() {
      if (!!this.$refs.subscriptionGroupName) {
        this.$refs.subscriptionGroupName.validate(true);
      }
      if (!!this.$refs.selectOwnerInput) {
        this.$refs.selectOwnerInput.validate(true);
      }
    },
    /**
     * Handle the Subscription Group Editor open trigger
     */
    openGroupEditorHandler() {
      this.showSubGroupEditor = true;
    },
    /**
     * Handle the Subscription Group Editor close trigger
     */
    closeGroupEditorHandler() {
      this.showSubGroupEditor = false;
      this.showConfirmDelete = false;
      this.selectedSubscriptionGroup = null;
      this.subscriptionGroupData = {
        name: '',
        description: '',
        active: false,
      };
      if (!!this.$refs.subscriptionGroupName) {
        this.$refs.subscriptionGroupName.reset();
      }
    },
    /**
     * Save the subscription group
     * - check if in Edit mode and perform create or edit api calls
     * respectively
     * @returns {Promise<void>}
     */
    async saveSubscriptionGroup() {
      this.saving = true;
      const subGroupData = {
        name: this.subscriptionGroupData.name,
        description: this.subscriptionGroupData.description,
        active: this.subscriptionGroupData.active,
      };
      try {
        if (!!this.editMode) {
          await NotificationService.NotificationGroups.put(this.subscriptionGroupData);
          await this.prepareData();
          this.closeGroupEditorHandler();
          this.$notifier.success(`Subscription Group "${subGroupData.name}" changes saved successfully`);
          this.$emit("refresh");
        } else {
          await NotificationService.NotificationGroups.post(this.subscriptionGroupData);
          await this.prepareData();
          this.closeGroupEditorHandler();
          this.$notifier.success(`Subscription Group "${subGroupData.name}" created successfully`);
        }
        await this.prepareData();
      } catch (error) {
        console.error(error);
        this.$notifier.error(`Failed to create Subscription Group "${subGroupData.name}"`);
      }
      this.saving = false;
    },
    /**
     * Handle the channel assignment trigger for a Subscription Group
     * @param groupData {Object} - the Data of the subscription group to assign a channel to
     */
    assignChannelSubscriptionGroupHandler(groupData) {
      // console.log('groupData in assignChannelSubscriptionGroupHandler', groupData);
      this.selectedSubscriptionGroup = cloneDeep(groupData);
      this.showAssignChannelDialog = true;
      this.showAssignChannelDialog = true;
    },
    /**
     * Handle the Edit subscription group option click
     * @param groupData {Object} - the Data of the subscription group to edit
     */
    editSubscriptionGroupHandler(groupData) {
      this.selectedSubscriptionGroup = groupData;

      this.subscriptionGroupData = {
        id: groupData.id,
        name: groupData.name,
        description: groupData.description,
        owner: groupData.owner,
        active: groupData.active,
      };
      this.showSubGroupEditor = true;
    },
    /**
     * Handle the Delete subscription group option click
     * @param groupIndex {number} - the index of the subscription group to be deleted
     */
    deleteSubscriptionGroupHandler(groupIndex) {
      this.subscriptionGroupData = {
        id: this.getSubscriptionGroups[groupIndex].id,
        name: this.getSubscriptionGroups[groupIndex].name,
        description: this.getSubscriptionGroups[groupIndex].description,
        active: this.getSubscriptionGroups[groupIndex].active,
      };
      this.showConfirmDelete = true;
    },
    /**
     * Delete a subscription group
     * @returns {Promise<void>}
     */
    async deleteSubscriptionGroup() {
      this.saving = true;
      try {
        await NotificationService.NotificationGroups.delete(this.subscriptionGroupData.id);
        await this.prepareData();
        this.$notifier.success(`Subscription Group "${this.subscriptionGroupData.name}" deleted`);
        this.closeGroupEditorHandler();
        await this.prepareData();
      } catch (error) {
        console.error(error);
        this.$notifier.error(`Failed to delete Subscription Group "${this.subscriptionGroupData.name}"`);
      }
      this.saving = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.notification-settings-cont {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

.notification-settings-section {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  justify-content: center;
  align-items: center;
  padding: 12px 24px;
}

.add-subscription-clickable {
  text-align: center;
  opacity: 0.7;
  cursor: pointer;
  transition: opacity ease-in-out 0.3s;
  width: 100%;
  max-width: 720px;
  padding: 4px 8px;
  border-radius: 6px;
  background-color: #383c40;

  &.dark {
    background-color: #383c40;
    box-shadow: 1px 0 4px rgba(0, 0, 0, 0.5)
  }

  &.light {
    background-color: #dbdbdb;
    box-shadow: 1px 0 2px rgba(0, 0, 0, 0.2);
  }

  &:hover {
    opacity: 1;
  }
}

.no-results {
  font-style: italic;
  text-align: center;
  opacity: 0.6;
  padding: 8px 16px;
}
</style>